/**
 * Creator Pro Buffet — the product-first checkout (Phase B, Jul 2026).
 *
 * Two modes on one screen:
 *   • SIMPLE (default)  → a clean, professional offer. Leads with the day-one
 *     deal; features are expandable value descriptions. One button: "Unlock
 *     Creator Pro" (the bundle — net of anything already owned).
 *   • CUSTOMIZE (toggle) → checkboxes + per-item prices + a live total. Minimum
 *     Workbench is the locked base. A "Select everything" quick-check.
 *
 * Ownership-aware (Randy): tools the user already bought render as OWNED
 * (struck through, not selectable, never re-charged). The bundle price is
 * discounted by the value they already own. `preselect` pre-checks a specific
 * feature (used when a locked control opens this popup).
 *
 * Server-side trust: the browser only sends chosen leaf SKU keys (or asks for
 * the full bundle) — the backend recomputes the authoritative charge (and drops
 * owned SKUs) before hitting Stripe.
 */
import { useEffect, useMemo, useState } from "react";
import { Crown, Check, CaretRight, CaretDown, Lock, ArrowRight, Sliders, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (cents) => (cents === null || cents === undefined ? "—"
  : `$${((cents || 0) / 100).toFixed(0)}`);

// Flatten catalog suites → leaf SKUs (items + their children) in tree order.
function flatten(catalog) {
  const leaves = [];
  for (const suite of catalog?.suites || []) {
    for (const item of suite.items || []) {
      leaves.push({ ...item, requires: item.requires || [], suiteKey: suite.key, depth: 0 });
      for (const ch of item.children || []) {
        leaves.push({ ...ch, requires: ch.requires || [], suiteKey: suite.key, depth: 1, parentKey: item.key });
      }
    }
  }
  return leaves;
}

export default function CreatorProBuffet({ status, onCheckout, redirecting, owned = [], preselect = null, initialCustomize = false, trial = null, onTrialCommit, onTrialRevoke, onTrialBundle, trialBusy = null }) {
  const [catalog, setCatalog] = useState(null);
  const [customize, setCustomize] = useState(!!initialCustomize);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());

  const ownedSet = useMemo(() => new Set(owned || []), [owned]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/creator-pro/catalog");
        setCatalog(r.data?.catalog || r.data);
      } catch (e) {
        toast.error("Could not load the Creator Pro menu.", { id: "buffet-load-error" });
      }
    })();
  }, []);

  const leaves = useMemo(() => (catalog ? flatten(catalog) : []), [catalog]);
  const byKey = useMemo(() => Object.fromEntries(leaves.map((l) => [l.key, l])), [leaves]);
  const childrenMap = useMemo(() => {
    const m = {};
    for (const l of leaves) for (const p of l.requires) (m[p] = m[p] || []).push(l.key);
    return m;
  }, [leaves]);

  const isLocked = (l) => !!(l.locked || l.required);
  const isOwned = (l) => ownedSet.has(l.key);

  // Seed the initial selection once the catalog + ownership are known.
  useEffect(() => {
    if (!catalog) return;
    const all = flatten(catalog);
    const byK = Object.fromEntries(all.map((l) => [l.key, l]));
    const baseUnowned = all.filter((l) => (l.locked || l.required) && !l.needs_code && !ownedSet.has(l.key)).map((l) => l.key);
    if (preselect && preselect.length) {
      // The tapped feature + its required ancestors + the base (minus owned).
      const sel = new Set(baseUnowned);
      const stack = [...preselect];
      while (stack.length) {
        const k = stack.pop();
        const node = byK[k];
        if (!node || node.needs_code) continue;
        if (!ownedSet.has(k)) sel.add(k);
        for (const p of node.requires || []) stack.push(p);
      }
      setSelected(sel);
      setCustomize(true);
    } else if (ownedSet.size > 0) {
      // Returning partial owner: start clean — only the (unowned) required base
      // is forced on; they opt-in to the tools they skipped. (Randy, Jul 2026.)
      setSelected(new Set(all.filter((l) => (l.locked || l.required) && !l.needs_code && !ownedSet.has(l.key)).map((l) => l.key)));
      setCustomize(!!initialCustomize);
    } else {
      // Newcomer: pre-check everything addable — shows the full value of getting the lot.
      setSelected(new Set(all.filter((l) => !l.needs_code && !ownedSet.has(l.key)).map((l) => l.key)));
      setCustomize(!!initialCustomize);
    }
  }, [catalog, preselect, ownedSet, initialCustomize]);

  const toggle = (key) => {
    const l = byKey[key];
    if (!l || isLocked(l) || l.needs_code || isOwned(l)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        const stack = [key]; // turn off + every dependent descendant
        while (stack.length) {
          const k = stack.pop();
          next.delete(k);
          for (const c of childrenMap[k] || []) stack.push(c);
        }
      } else {
        next.add(key); // turn on + every required ancestor (skip owned)
        const reqs = [...(byKey[key].requires || [])];
        while (reqs.length) {
          const p = reqs.pop();
          if (byKey[p] && !byKey[p].needs_code && !ownedSet.has(p)) {
            next.add(p);
            reqs.push(...(byKey[p].requires || []));
          }
        }
      }
      for (const ll of leaves) if (isLocked(ll) && !ownedSet.has(ll.key)) next.add(ll.key); // required base always on
      return next;
    });
  };

  // "Select everything" — every addable (unowned, buildable) SKU.
  const selectableKeys = useMemo(
    () => leaves.filter((l) => !l.needs_code && !ownedSet.has(l.key)).map((l) => l.key),
    [leaves, ownedSet]
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleSelectAll = () => {
    setSelected(() => {
      if (allSelected) {
        const next = new Set();
        for (const l of leaves) if (isLocked(l) && !ownedSet.has(l.key)) next.add(l.key);
        return next;
      }
      return new Set(selectableKeys);
    });
  };

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const bundleCents = catalog?.bundle?.day1_cents || 0;
  // Feb 2026 (Randy pt-regular-cents-removal) — anchor for the strike-through
  // and Pay-It-Forward math is now the honest à-la-carte sum (buy every SKU
  // separately at day-one prices) instead of the retired `regular_cents`
  // marketing field. Same visual effect, real math backing it.
  const alacarteAnchorCents = useMemo(
    () => leaves.reduce((s, l) => s + (l.day1_cents || 0), 0),
    [leaves]
  );
  const ownedValueCents = useMemo(
    () => leaves.filter((l) => ownedSet.has(l.key)).reduce((s, l) => s + (l.day1_cents || 0), 0),
    [leaves, ownedSet]
  );
  const bundleNetCents = Math.max(0, bundleCents - ownedValueCents);
  const savingsCents = Math.max(0, alacarteAnchorCents - bundleCents);
  const hasOwned = ownedValueCents > 0;
  const alacarteCents = useMemo(
    () => leaves.filter((l) => selected.has(l.key) && !ownedSet.has(l.key)).reduce((s, l) => s + (l.day1_cents || 0), 0),
    [leaves, selected, ownedSet]
  );

  if (!catalog) {
    return (
      <div className="text-[#E5E5E0]/70 text-sm py-6" data-testid="buffet-loading">
        Loading the Creator Pro menu…
      </div>
    );
  }

  const monthly = fmt$(status?.creator_monthly_fee_cents);

  // Oct 2026 (TRIAL_PLAN.md §8.6) — during a trial every row can be added to
  // the day-14 bill instead of bought today. Frozen or cancelled trials can't
  // change the list, so the controls disappear rather than fail on click.
  const committedSet = new Set((trial?.committed_skus || []).map((r) => r.sku));
  const trialLive = !!trial?.live && !trial.cancel_at_period_end && !trial.commitments_frozen;
  const trialBundled = !!trial?.committed_bundle;

  // One feature row (item or child). Renders differently per mode.
  const Row = ({ node, depth }) => {
    const open = expanded.has(node.key);
    const checked = selected.has(node.key);
    const locked = isLocked(node);
    const ownedRow = isOwned(node);
    const soon = !!node.needs_code;
    const hasDesc = !!node.description;
    return (
      <div
        data-testid={`buffet-row-${node.key}`}
        className={depth > 0 ? "ml-5 pl-3 border-l border-white/10" : ""}
      >
        <div className="flex items-center gap-2.5 py-1.5">
          {customize && (
            ownedRow ? (
              <span
                data-testid={`buffet-owned-${node.key}`}
                aria-label="Owned"
                className="w-[18px] h-[18px] rounded shrink-0 flex items-center justify-center bg-[#4A5D4E] border border-[#4A5D4E]"
              >
                <Check size={12} weight="bold" className="text-white" />
              </span>
            ) : (
              <button
                type="button"
                onClick={() => toggle(node.key)}
                disabled={locked || soon}
                data-testid={`buffet-check-${node.key}`}
                aria-checked={checked}
                role="checkbox"
                className={`w-[18px] h-[18px] rounded shrink-0 flex items-center justify-center border transition-colors ${
                  checked ? "bg-[#C16E5A] border-[#C16E5A]" : "bg-transparent border-white/30"
                } ${locked || soon ? "opacity-60 cursor-not-allowed" : "hover:border-[#C16E5A] cursor-pointer"}`}
              >
                {checked && <Check size={12} weight="bold" className="text-white" />}
                {locked && !checked && <Lock size={10} weight="fill" className="text-white/70" />}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => hasDesc && toggleExpand(node.key)}
            data-testid={`buffet-expand-${node.key}`}
            className="flex-1 flex items-center gap-1.5 text-left group"
          >
            {hasDesc &&
              (open ? (
                <CaretDown size={12} className="text-[#A8A8A0] shrink-0" />
              ) : (
                <CaretRight size={12} className="text-[#A8A8A0] shrink-0 group-hover:text-[#C16E5A]" />
              ))}
            <span className={`text-sm ${ownedRow ? "line-through text-[#E5E5E0]/45" : depth > 0 ? "text-[#E5E5E0]/85" : "text-[#FDFCF8]"}`}>
              {node.name}
            </span>
            {ownedRow ? (
              <span className="text-[9px] uppercase tracking-[0.15em] bg-[#4A5D4E]/40 text-[#CDE0CF] px-1.5 py-0.5 rounded" data-testid={`buffet-owned-tag-${node.key}`}>
                Owned
              </span>
            ) : (
              <>
                {locked && (
                  <span className="text-[9px] uppercase tracking-[0.15em] bg-white/10 text-[#E5E5E0] px-1.5 py-0.5 rounded">
                    Required
                  </span>
                )}
                {soon && (
                  <span className="text-[9px] uppercase tracking-[0.15em] bg-white/10 text-[#A8A8A0] px-1.5 py-0.5 rounded">
                    Coming soon
                  </span>
                )}
              </>
            )}
          </button>
          {customize && (
            <span
              className={`text-xs tabular-nums shrink-0 ${ownedRow ? "text-[#CDE0CF]" : "text-[#E5E5E0]/70"}`}
              data-testid={`buffet-price-${node.key}`}
            >
              {ownedRow ? "Owned" : fmt$(node.day1_cents)}
            </span>
          )}
          {trialLive && !ownedRow && !soon && (
            locked || trialBundled || committedSet.has(node.key) ? (
              <span
                data-testid={`buffet-include-${node.key}`}
                className="text-[9px] uppercase tracking-[0.14em] shrink-0 px-2 py-1 rounded bg-[#4A5D4E] text-white"
              >
                {locked ? "Included" : "On day 14"}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onTrialCommit?.(node.key, node.day1_cents)}
                disabled={trialBusy === node.key}
                data-testid={`buffet-include-${node.key}`}
                className="text-[9px] uppercase tracking-[0.14em] shrink-0 px-2 py-1 rounded border border-[#FFC24A]/50 text-[#FFC24A] hover:bg-[#FFC24A]/15 disabled:opacity-50 transition-colors"
              >
                {trialBusy === node.key ? "Adding…" : "Include at day 14"}
              </button>
            )
          )}
          {trialLive && committedSet.has(node.key) && !locked && !trialBundled && (
            <button
              type="button"
              onClick={() => onTrialRevoke?.(node.key)}
              disabled={trialBusy === node.key}
              data-testid={`buffet-exclude-${node.key}`}
              className="text-[#A8A8A0] hover:text-[#E0A08A] shrink-0 disabled:opacity-50"
              title={`Take ${node.name} off the day-14 bill`}
            >
              <X size={12} />
            </button>
          )}
        </div>
        {open && hasDesc && (
          <p
            className={`text-[12px] leading-relaxed text-[#E5E5E0]/70 pb-2 ${customize ? "pl-[28px]" : "pl-[18px]"}`}
            data-testid={`buffet-desc-${node.key}`}
          >
            {node.description}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="relative z-10" data-testid="creator-pro-buffet">
      {/* Header */}
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] mb-2 flex items-center gap-2">
        <Crown size={12} weight="duotone" /> Creator Pro
      </div>
      <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight">
        Unlock the <em className="text-[#C16E5A]">full My Silo toolkit</em>
      </h3>
      <p className="text-sm text-[#E5E5E0] mt-2 max-w-xl">
        A one-time upgrade that unlocks every advanced building tool. Your {monthly}/mo Creator
        subscription continues unchanged.
      </p>
      {hasOwned && (
        <p className="text-[12px] text-[#CDE0CF] mt-2" data-testid="buffet-owned-summary">
          You already own {fmt$(ownedValueCents)} of Creator Pro — add the rest below.
        </p>
      )}
      {trial?.live && (
        <div
          className="mt-3 rounded-md border border-[#FFC24A]/30 bg-[#FFC24A]/[0.08] px-3 py-2"
          data-testid="buffet-trial-total"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFC24A]">
            Your day-{trial.trial_days || 14} total so far
          </div>
          <div className="text-sm text-[#FDFCF8] mt-0.5 tabular-nums">
            {fmt$(trial.running_total_cents)}
            <span className="text-[11px] text-[#E5E5E0]/75 tabular-nums">
              {" "}· {fmt$(trial.subscription_cents)} subscription
              {trial.committed_cents > 0 ? ` + ${fmt$(trial.committed_cents)} of tools you chose to keep` : " · no tools kept yet"}
            </span>
          </div>
          <p className="text-[11px] text-[#E5E5E0]/70 mt-1">
            {trial.cancel_at_period_end
              ? "Your trial is cancelled — these selections are void and nothing will be charged."
              : trial.commitments_frozen
                ? "This list is locked in for the final hour before your trial ends."
                : "Nothing is charged today. Anything you include can be removed until an hour before the trial ends."}
          </p>
        </div>
      )}

      {/* Simple / Customize toggle */}
      <div className="mt-4 inline-flex items-center rounded-md bg-white/10 p-0.5" data-testid="buffet-mode-toggle">
        <button
          type="button"
          onClick={() => setCustomize(false)}
          data-testid="buffet-mode-simple"
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            !customize ? "bg-[#FDFCF8] text-[#1C1B1A]" : "text-[#E5E5E0] hover:text-white"
          }`}
        >
          Simple
        </button>
        <button
          type="button"
          onClick={() => setCustomize(true)}
          data-testid="buffet-mode-customize"
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5 ${
            customize ? "bg-[#FDFCF8] text-[#1C1B1A]" : "text-[#E5E5E0] hover:text-white"
          }`}
        >
          <Sliders size={12} weight="bold" /> Customize
        </button>
      </div>
      <div className="flex items-center justify-between gap-3 mt-2">
        <p className="text-[11px] text-[#A8A8A0]">
          {customize
            ? "Pick exactly what you want. Minimum Workbench is the required base."
            : "Explore everything Creator Pro includes — tap any feature to read more."}
        </p>
        {customize && (
          <button
            type="button"
            onClick={toggleSelectAll}
            data-testid="buffet-select-all"
            aria-checked={allSelected}
            role="checkbox"
            className="inline-flex items-center gap-1.5 text-[11px] text-[#E5E5E0]/85 hover:text-white shrink-0"
          >
            <span className={`w-[15px] h-[15px] rounded border flex items-center justify-center transition-colors ${allSelected ? "bg-[#C16E5A] border-[#C16E5A]" : "border-white/30"}`}>
              {allSelected && <Check size={10} weight="bold" className="text-white" />}
            </span>
            Select everything
          </button>
        )}
      </div>

      {/* Feature tree */}
      <div className="mt-4 space-y-4">
        {(catalog.suites || []).map((suite) => (
          <div key={suite.key} data-testid={`buffet-suite-${suite.key}`}>
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-[11px] uppercase tracking-[0.2em] text-[#E5E5E0]/80 font-medium">
                {suite.name}
              </h4>
              {suite.required && (
                <span className="text-[9px] uppercase tracking-[0.15em] bg-[#C16E5A]/25 text-[#F0C4B8] px-1.5 py-0.5 rounded">
                  Required
                </span>
              )}
            </div>
            <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-1.5">
              {(suite.items || []).map((item) => (
                <div key={item.key}>
                  <Row node={item} depth={0} />
                  {(item.children || []).map((ch) => (
                    <Row key={ch.key} node={ch} depth={1} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer / pricing */}
      <div className="mt-6 pt-5 border-t border-white/10">
        {!customize ? (
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                {savingsCents > 0 && !hasOwned && (
                  <span className="text-[#A8A8A0] line-through text-lg tabular-nums" data-testid="buffet-anchor-price">
                    {fmt$(alacarteAnchorCents)}
                  </span>
                )}
                <span className="font-serif-display text-4xl text-[#FDFCF8] tabular-nums" data-testid="buffet-bundle-price">
                  {fmt$(bundleNetCents)}
                </span>
              </div>
              <p className="text-[11px] text-[#C16E5A] mt-1">
                {hasOwned
                  ? `Get the rest · ${fmt$(bundleCents)} bundle − ${fmt$(ownedValueCents)} you own`
                  : `Day-one deal${savingsCents > 0 ? ` · save ${fmt$(savingsCents)}` : ""} · one-time payment`}
              </p>
            </div>
            <button
              onClick={() => onCheckout({ full_bundle: true, selected_skus: null })}
              disabled={redirecting}
              data-testid="buffet-bundle-checkout"
              className="bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-6 py-3 rounded-md flex items-center gap-2 text-sm font-medium disabled:opacity-60 transition-colors"
            >
              {redirecting ? "Opening Stripe…" : <>{hasOwned ? "Get the rest" : "Unlock Creator Pro"} <ArrowRight size={16} /></>}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.2em] text-[#A8A8A0]">Your total</span>
              <span className="font-serif-display text-3xl text-[#FDFCF8] tabular-nums" data-testid="buffet-total">
                {fmt$(alacarteCents)}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={() => onCheckout({ full_bundle: false, selected_skus: [...selected] })}
                disabled={redirecting || alacarteCents <= 0}
                data-testid="buffet-alacarte-checkout"
                className="flex-1 bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-3 rounded-md flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                {redirecting ? "Opening Stripe…" : <>Checkout selected · {fmt$(alacarteCents)} <ArrowRight size={16} /></>}
              </button>
              {/* Oct 2026 (HOMEPAGE_PRICING_REVAMP.md §4) — Randy went looking
                  for this button and found it missing: it only appeared once the
                  selection cost MORE than the bundle. Now it always shows, and
                  says which of the two it is. Below the bundle it is an honest
                  upsell with the difference named; at or above it, it is the
                  cheaper of the two and says so. Never pretends to save money
                  it doesn't. */}
              {bundleNetCents > 0 && (
                <button
                  onClick={() => onCheckout({ full_bundle: true, selected_skus: null })}
                  disabled={redirecting}
                  data-testid="buffet-bundle-shortcut"
                  className="flex-1 border border-white/25 text-[#FDFCF8] hover:bg-white/10 px-5 py-3 rounded-md text-sm font-medium disabled:opacity-60 transition-colors"
                >
                  {hasOwned ? "Get the rest" : "Get everything"} · {fmt$(bundleNetCents)}{" "}
                  {alacarteCents > bundleNetCents ? (
                    <span className="text-[#C16E5A]">(best value)</span>
                  ) : alacarteCents > 0 ? (
                    <span className="text-[#A8A8A0]">(+{fmt$(bundleNetCents - alacarteCents)})</span>
                  ) : null}
                </button>
              )}
            </div>
          </div>
        )}
        <p className="text-[10px] text-[#A8A8A0] mt-3">One-time payment via Stripe. No recurring Pro fee.</p>
        {trialLive && !trialBundled && (
          <button
            type="button"
            onClick={() => onTrialBundle?.(trial.bundle_cents)}
            disabled={trialBusy === "bundle" || !trial.bundle_cents}
            data-testid="buffet-include-bundle"
            className="mt-3 w-full border border-[#FFC24A]/50 text-[#FFC24A] hover:bg-[#FFC24A]/15 px-5 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
          >
            {trialBusy === "bundle"
              ? "Adding…"
              : `Or keep everything on day ${trial.trial_days || 14} · ${fmt$(trial.bundle_cents)}`}
          </button>
        )}
      </div>
    </div>
  );
}
